import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../Dashboard.module.css";
import { DataGrid } from "@mui/x-data-grid";
import {
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  TextField,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Cancel from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonIcon from "@mui/icons-material/Person";
import {
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import NotificationSnackbar from "../../../components/NotificationSnackbar";
import { useAuthApi } from "../../../hooks/useAuthApi";
import SearchIcon from "@mui/icons-material/Search";
import { useClientsApi } from "../../../hooks/useClientsApi";
import { useUsersApi } from "../../../hooks/useUsersApi";
import { sanitizeInput } from "../../../utils/inputSanitizer";

export default function UserList() {
  const navigate = useNavigate();
  const { resendVerificationEmail, proxyLogin } = useAuthApi();
  const { clients, fetchClients } = useClientsApi();
  const { loading, users, fetchUsers, createInitialUser, updateUser } =
    useUsersApi();

  // ...existing code...
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", clientId: [] });
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Disable");

  // ...existing code...
  const [error, setError] = useState("");

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Show notification helper
  const showNotification = (message, severity = "info") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  // Handle proxy login
  const handleProxyLogin = async (userId) => {
    try {
      console.log(`Initiating proxy login for user ID: ${userId}`);
      
      const result = await proxyLogin({ 
        targetUserId: userId,
        reason: "Administrative assistance" 
      });
      
      if (result && result.success) {
        showNotification(
          `Successfully switched to ${result.proxyUser.name} (${result.proxyUser.email})`, 
          "success"
        );
        // Navigation will be handled by the proxyLogin function
      } else {
        showNotification(
          result?.error || "Failed to initiate proxy login", 
          "error"
        );
      }
    } catch (error) {
      console.error("Proxy login error:", error);
      showNotification("Failed to initiate proxy login", "error");
    }
  };

  const columns = [
    { field: "name", headerName: "Name", width: 200 },
    {
      field: "email",
      headerName: "Email",
      width: 250,
      cellClassName: "email-cell",
    },
    {
      field: "registrationStatus",
      headerName: "Status",
      width: 140,
      renderCell: (params) => {
        const status = params.row.registrationStatus;
        const getStatusColor = () => {
          if (status === "Enable" || status === "Active") return "success";
          if (status === "Pending") return "warning";
          return "error";
        };

        return (
          <Chip
            label={status}
            color={getStatusColor()}
            size="small"
            sx={{
              fontSize: "0.775rem",
              width: 70, // fixed width
              justifyContent: "center", // center text
              textTransform: "capitalize", // optional: make label look cleaner
            }}
          />
        );
      },
    },
    {
      field: "edit",
      headerName: "Edit",
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          onClick={() => navigate(`/superadmin/users/${params.row.id}/edit`)}
          sx={{
            color: "var(--color-icon)",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.1)",
            },
          }}
        >
          <EditIcon />
        </IconButton>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const status = params.row.registrationStatus;
        
        // Show Resend Invite for Pending or Invited users
        if (status === "Pending" || status === "Invited") {
          return (
            <IconButton
              color="primary"
              size="small"
              title="Resend Invite"
              onClick={async () => {
                const result = await resendVerificationEmail(params.row.id);
                if (result) {
                  showNotification(
                    "Verification email resent successfully!",
                    "success"
                  );
                } else {
                  showNotification(
                    "Failed to resend verification email.",
                    "error"
                  );
                }
              }}
              sx={{
                color: "var(--color-icon)",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.1)",
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          );
        }
        
        // Show Proxy View for Active/Enable users
        if (status === "Enable" || status === "Active") {
          return (
            <IconButton
              color="primary"
              size="small"
              title="Login as this user"
              onClick={() => handleProxyLogin(params.row.id)}
              sx={{
                color: "#4caf50", // Green color for proxy
                "&:hover": {
                  backgroundColor: "rgba(76, 175, 80, 0.1)",
                },
              }}
            >
              <PersonIcon />
            </IconButton>
          );
        }
        
        return null; // Don't show any action for other statuses
      },
    },
  ];

  // Close notification
  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    fetchUsers();
    fetchClients();
  }, []);

  // Handle form input
  const handleChange = (e) => {
    const { name, value } = e.target;

    let finalValue = value;
    if (name === "name") {
      finalValue = sanitizeInput(value); // use sanitized value
    }

    if (name === "clientId") {
      setForm({
        ...form,
        clientId: Array.isArray(finalValue) ? finalValue : [finalValue],
      });
    } else {
      setForm({ ...form, [name]: finalValue });
    }
  };

  // Add or update user
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    // Send clientIds array for multi-client support
    const payload = {
      name: form.name,
      email: form.email,
      clientIds:
        form.clientId && form.clientId.length > 0
          ? form.clientId.map((id) => parseInt(id, 10))
          : [],
    };

    if (editingId) {
      await updateUser(editingId, payload);
    } else {
      // Use create-initial endpoint - sends verification email, no password required
      const result = await createInitialUser(payload);
      if (result.success) {
        showNotification("User created successfully", "success");
      } else if (!result.success && result.error === "Email already exists.") {
        showNotification(result.error, "error");
        return;
      } else {
        showNotification("Failed to create user", "error");
        return;
      }
    }
    setForm({ name: "", email: "", clientId: [] });
    setEditingId(null);
    setShowCreateForm(false);
    fetchUsers();
  };

  function getFilteredUsers(users, search) {
    if (!search) return users;
    const lower = search.toLowerCase();
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(lower) ||
        u.email?.toLowerCase().includes(lower)
    );
  }

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 30,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h1 className={styles.dashboardTitle}>USERS</h1>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            position: "relative",
            minHeight: 40,
            height: 48,
            padding: "0 4px",
          }}
        >
          <FormControlLabel
            labelPlacement="start"
            control={
              <Switch
                checked={statusFilter === "Enable"}
                onChange={() => {
                  setStatusFilter(
                    statusFilter === "Enable" ? "Disable" : "Enable"
                  );
                  statusFilter === "Enable" ? fetchUsers() : fetchUsers(false);
                }}
                sx={{
                  color: "var(--color-primary)",
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#43a047", // light green
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#43a047",
                  },
                }}
              />
            }
            label={
              <span
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  color: "var(--color-header)",
                }}
              >
                Show Disabled
              </span>
            }
          />
          {/* Search user filter always visible */}
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              minWidth: 220,
              height: 40,
            }}
          >
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                padding: "8px 24px 8px 40px",
                borderRadius: 4,
                border: "1px solid #ccc",
                fontSize: "0.98rem",
                width: "100%",
                height: 40,
                boxSizing: "border-box",
              }}
            />
            <span
              style={{
                position: "absolute",
                left: 12,
                top: "55%",
                transform: "translateY(-50%)",
                color: "#888",
                pointerEvents: "none",
              }}
            >
              <SearchIcon />
            </span>
          </div>
          <div style={{ position: "relative", display: "inline-block" }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              onClick={() => {
                setShowCreateForm(true);
                setEditingId(null);
                setForm({ name: "", email: "", clientId: [] });
              }}
              sx={{
                width: "100%",
                backgroundColor: "var(--color-primary)",
                "&:hover": { backgroundColor: "var(--color-secondary)" },
              }}
            >
              Add User
            </Button>
          </div>
          {showCreateForm && (
            <Dialog
              open={showCreateForm}
              onClose={() => setShowCreateForm(false)}
              maxWidth="xs"
              fullWidth
              PaperProps={{
                sx: {
                  minWidth: 300,
                  maxWidth: 450,
                  p: 1,
                  position: "relative",
                },
              }}
            >
              {/* Absolute positioned close button */}
              <IconButton
                aria-label="close"
                onClick={() => setShowCreateForm(false)}
                size="small"
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: "var(--color-close-icon)",
                  zIndex: 1,
                }}
              >
                <Cancel fontSize="small" />
              </IconButton>
              <DialogTitle sx={{ pr: 4 }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="var(--color-primary)"
                >
                  {editingId ? "Update User" : "Add a New User".toUpperCase()}
                </Typography>
              </DialogTitle>
              <form onSubmit={handleSubmit}>
                <DialogContent sx={{ pt: 1 }}>
                  {error && (
                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                      {error}
                    </Typography>
                  )}
                  <TextField
                    fullWidth
                    label="Name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    inputProps={{ maxLength: 30 }}
                    sx={{ mb: 2 }}
                    variant="outlined"
                    size="small"
                    required
                    autoFocus
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    inputProps={{ maxLength: 50 }}
                    sx={{ mb: 2 }}
                    variant="outlined"
                    size="small"
                    required
                  />
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Client Access
                  </Typography>
                  <Select
                    displayEmpty
                    multiple
                    name="clientId"
                    value={form.clientId || []}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        clientId: Array.isArray(e.target.value)
                          ? e.target.value
                          : [e.target.value],
                      })
                    }
                    input={<OutlinedInput size="small" />}
                    renderValue={(selected) => {
                      if (!selected || selected.length === 0) return "";
                      const selectedClients = clients.filter((c) =>
                        selected.includes(c.id.toString())
                      );
                      return selectedClients.map((c) => c.name).join(", ");
                    }}
                    sx={{ width: "100%", mb: 2 }}
                    required
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 250,
                          overflowY: "auto",
                        },
                      },
                    }}
                  >
                    {clients.map((client) => (
                      <MenuItem key={client.id} value={client.id.toString()}>
                        <Checkbox
                          checked={form.clientId.includes(client.id.toString())}
                        />
                        <ListItemText primary={client.name} />
                      </MenuItem>
                    ))}
                  </Select>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    sx={{
                      backgroundColor: "var(--color-primary)",
                      "&:hover": { backgroundColor: "var(--color-secondary)" },
                    }}
                  >
                    {editingId ? "Update" : "Create User"}
                  </Button>
                </DialogContent>
              </form>
            </Dialog>
          )}
        </div>
      </div>
      <Paper sx={{ height: 580, width: "100%", borderRadius: 2, boxShadow: 2,cursor: 'pointer' }}>
        <DataGrid
          rows={getFilteredUsers(users, search)}
           onRowDoubleClick={(params) => navigate(`/superadmin/users/${params.row.id}/edit`)}
          columns={columns}
          rowHeight={36}
          loading={loading}
          initialState={{
            columns: {
              columnVisibilityModel: {
                id: false, // 👈 hides the 'id' column
              },
            },
            pagination: { paginationModel: { page: 0, pageSize: 15 } },
          }}
          pageSizeOptions={[15, 30, 50]}
          disableRowSelectionOnClick
          sx={{
            border: 0,
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "14px",
              fontWeight: "bold",
            },
          }}
        />
      </Paper>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={handleCloseNotification}
      />
    </div>
  );
}
